<template>
    <div class="template-data-editor">
        <form v-if="rootElement" v-on:submit.prevent="handleSubmit">
            <ElementSelector :element="rootElement" :elData="initialData" :onChange="handleChange" :errorInfo="errorInfo" :keyTail="rootKeyTail" :retrieveData="retrieveData"></ElementSelector>
            <div class="row mt-3">
                <div class="col-md-8 offset-md-2">
                    <div v-if="hasErrors" class="mb-3 text-center">
                        <strong class="invalid-feedback">Verifique os erros acima</strong>
                    </div>
                    <button type="submit" class="btn btn-primary btn-block btn-lg">Salvar</button>
                </div>
            </div>
        </form>
        <div v-else>
            <em>Carregando...</em>
        </div>
    </div>
</template>

<script>
import ElementSelector from './ElementSelector';
import { requestJson } from '../../helpers';

export default {
    provide() {
        return {
            newPurchaseFlowEnabled: this.newPurchaseFlowEnabled,
        };
    },
    props: {
        dataFormatUrl: String,
        formatEntitiesUrl: String,
        templateDataUrl: String,
        updateTemplateDataUrl: String,
        getTemplateImagesUrl: String,
        uploadTemplateImageUrl: String,
        newPurchaseFlowEnabled: { type: Boolean, default: false },
    },
    data(){
        return {
            rootElement: null,
            initialData: null,
            finalData: null,
            errors: {},
            entityOptions: [],
            apientityOptions: {},
        };
    },
    computed: {
        errorInfo(){
            return [this.errors, key => this.ackError(key)];
        },
        rootKeyTail(){
            return ['root'];
        },
        hasErrors(){
            return Object.values(this.errors).reduce((c, v) => c + (v ? 1 : 0), 0) > 0; // Check whether there is some error to display
        }
    },
    methods: {
        async loadForm(){
            const [
                formatJson,
                dataJson
            ] = await Promise.all([
                requestJson(this.dataFormatUrl),
                requestJson(this.templateDataUrl)
            ]);

            this.initialData = dataJson;
            this.rootElement = formatJson;

            const kindMap = {};
            const apientityMap = {};
            this.traverseEntityElements(formatJson, kindMap, apientityMap);

            const kinds = Object.keys(kindMap).join(',');
            if(kinds){
                this.entityOptions = await requestJson(this.formatEntitiesUrl, { kinds });
            }

            this.apientityOptions = await this.buildApiEntityOptions(apientityMap);
        },
        traverseEntityElements(el, kindMap, apientityMap){
            switch(el.type){
                case 'entity':
                    kindMap[el.kind] = true;
                    break
                case 'apientity':
                    const key = this.serializeApiEntity(el);
                    apientityMap[key] = el;
                    break;
            }

            if(el.sub_elements){
                for(let sub of el.sub_elements){
                    this.traverseEntityElements(sub, kindMap, apientityMap);
                }
            }
        },
        serializeApiEntity(el){
            const fields = [
                'url',
                'id_field',
                'label_field',
                'limit_parameter',
                'page_parameter',
                'offset_parameter',
            ];

            return fields.map(f => el[f] ?? '').join('|');
        },
        async buildApiEntityOptions(apientityMap){
            const options = {};
            for(let key of Object.keys(apientityMap)){
                const el = apientityMap[key];
                options[key] = await this.loadElementApiEntityOptions(el);
            }

            return options;
        },
        async loadElementApiEntityOptions(el){
            const shouldPaginate = el.limit_parameter && (el.page_parameter || el.offset_parameter);
            const limit = 50;
            const items = [];

            let keepLoading = true;
            for (let x = 0; keepLoading; x++) {
                const url = new URL(el.url);

                if (shouldPaginate) {
                    const params = url.searchParams;
                    params.set(el.limit_parameter, limit.toString());

                    if (el.page_parameter) {
                        params.set(el.page_parameter, (x + 1).toString());
                    } else if (el.offset_parameter) {
                        params.set(el.offset_parameter, (x * limit).toString());
                    }

                    url.search = params;
                }

                let data = await requestJson(url.href, {}, 'get', true);
                if (el.array_field) {
                    data = this.getPropPath(data, el.array_field);
                }

                if (!Array.isArray(data)) {
                    console.error('API entity url did not return an array:', url.href);
                    return [];
                }

                for (let item of data) {
                    const value = (this.getPropPath(item, el.id_field) ?? '').toString();
                    const label = el.label_field ? (this.getPropPath(item, el.label_field) ?? value) : value;

                    items.push({ value, label });
                }

                keepLoading = shouldPaginate && data.length >= limit;
            }

            return items;
        },
        getPropPath(obj, path){
            const parts = path.split('.');
            for (let part of parts){
                if(!(part in obj)) {
                    return undefined;
                }

                obj = obj[part];
            }

            return obj;
        },

        handleChange(v){
            this.finalData = v;
        },
        async handleSubmit(ev){
            this.errors = {};

            const json = await requestJson(this.updateTemplateDataUrl, this.finalData, 'POST');
            this.receiveUpdateResponse(json);
        },
        receiveUpdateResponse(json){
            if(json.status == 'OK')
                return alert('Conteúdo atualizado!');
            else if(json.errors)
                this.errors = json.errors.reduce((c, err) => ({ ...c, [err.key]: err.code }), {});
            else
                return alert('Erro desconhecido! Tenta novamente em alguns instantes.');
        },
        ackError(key){
            this.errors = { ...this.errors, [key]: null };
        },

        retrieveData(cmd, ...args){
            switch(cmd){
                case 'entityOptions':
                    const [kind] = args;
                    for(let kindOpts of this.entityOptions){
                        if(kind === kindOpts.kind){
                            return kindOpts.items;
                        }
                    }
                    return [];
                case 'apiEntityOptions':
                    const [el] = args;
                    const key = this.serializeApiEntity(el);

                    return this.apientityOptions[key] || [];
                case 'imageOptions':
                    return requestJson(this.getTemplateImagesUrl);
                case 'uploadTemplateImage':
                    const [formData] = args;
                    return requestJson(this.uploadTemplateImageUrl, formData, 'POST');
            }

            return null;
        }
    },
    mounted(){
        this.loadForm();
    },
    components: {
        ElementSelector
    }
}
</script>

<style>
.template-data-editor {
    max-width: 700px;
}
</style>
